import { allSteps, stepEnum } from "@instacover/helpers"
import { loadOrt } from "@instacover/helpers"
import { getImgDataForTensor, Maybe } from "@instacover/utils"
import type { Tensor } from "onnxruntime-web"

import type { OrderedStepLocal, RealOrderedStep } from "@/services/dexie"

import { PhotoType, SessionConfig, SessionDetail } from "../../utils/types"
import { CarViewPredictResponse, ModelCombinedLabel, modelCombinedLabel, modelQualityLabel } from "./modelTypes"

export async function imgToTensor(img: HTMLImageElement): Promise<Tensor> {
  const { Tensor: TensorCtor } = await loadOrt()
  const { values, dims } = getImgDataForTensor(img)
  return new TensorCtor(values, dims)
}

export function isValidStep(value: string): value is PhotoType {
  for (const photoType of allSteps) {
    if (photoType === value) {
      return true
    }
  }

  return false
}

export function isCombinedPhotoType(value: string): value is ModelCombinedLabel {
  return value === modelCombinedLabel.VinOrOdometer
}

/**
 * Checks if a predicted step can still accept a photo, i.e. it either has no photo yet
 * or supports multiple photos. Used to avoid offering "you photographed a different step"
 * confirmation for a step that is already finished and can't take another photo.
 */
export function canUsePredictedStep(steps: RealOrderedStep[], predictedType: Maybe<string>): boolean {
  const predictedStep = steps.find((step) => step.name === predictedType)

  if (!predictedStep) {
    return false
  }

  const canUploadMultiplePhotos = predictedStep.maxMedia > 1 || predictedStep.maxMedia === -1

  return !predictedStep.isFinished || canUploadMultiplePhotos
}

/**
 * Odometer and VIN are often photographed on a digital display, so from_screen is ignored there (by step or by
 * predicted view). Ignored already in the model output, so neither the upload log nor browserModelResult carries it
 * and the fromScreenAttempt / forcedPhoto business rules stay consistent with the app.
 */
export function resolveIgnoredQualityLabels(
  step: PhotoType,
  carViewLabel: CarViewPredictResponse["carView"]["label"],
): CarViewPredictResponse["quality"]["label"][] {
  const isOdometerOrVinStep = step === stepEnum.Odometer || step === stepEnum.Vin
  const isOdometerOrVinPredicted =
    carViewLabel === stepEnum.Odometer || carViewLabel === modelCombinedLabel.VinOrOdometer

  return isOdometerOrVinStep || isOdometerOrVinPredicted ? [modelQualityLabel.FROM_SCREEN] : []
}

export function resolveIsCarViewPredictionConfident(
  prediction: Maybe<CarViewPredictResponse>,
  photoQualityValidations: SessionDetail["photoQualityValidations"],
) {
  if (!prediction) {
    return false
  }

  if (!photoQualityValidations.licensePlate && prediction.quality.label === modelQualityLabel.MISSING_LICENSE_PLATE) {
    // we dont care about quality confidence if we have license plate validation disabled
    return prediction.carView.confidence >= 0.5
  }

  return prediction.carView.confidence >= 0.5
}

/**
 * Checks if onnx document categorization model should be pre-loaded for the given session
 * based on presence of DocumentPhoto steps with documentValidityCheck enabled in session
 */
export function shouldPreloadDocumentCategorizationModel(params: {
  steps: OrderedStepLocal[] | SessionConfig["orderedSteps"]
}) {
  const { steps } = params
  return steps.some((step) => step.stepType === "DocumentPhoto" && step.documentValidityCheck === true)
}

/**
 * Checks if onnx car view categorization model should be pre-loaded for the given session
 * based on presence of VehiclePhoto steps with viewsCheck enabled in session
 */
export function shouldPreloadCarViewCategorizationModel(params: {
  steps: OrderedStepLocal[] | SessionConfig["orderedSteps"]
}) {
  const { steps } = params
  return steps.some((step) => step.stepType === "VehiclePhoto" && step.viewsCheck === true)
}
